import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { collection, getFirestore, onSnapshot, query, where } from 'firebase/firestore';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { IANA_TIMEZONES } from '../flows/triggers/scheduleContract';
import { getFlowAssignmentsByUserPublic } from '../../api/backendApi';
import { flowsApi } from '../../services/flowsApiService';
import {
  cancelReminder,
  createReminder,
  deleteReminder,
  dismissReminder,
  listReminderAlerts,
  listReminders,
  pauseReminder,
  resumeReminder,
  snoozeReminder,
  updateReminder,
} from '../../services/reminders/remindersApi';
import { isRemindersEnabledForUser } from '../../utils/remindersEntitlement';

const DEFAULT_REMINDER_TIMEZONE = 'America/Toronto';
const DEFAULT_REMINDER_LOCAL_TIME = '09:00';

const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

const sectionSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  p: { xs: 1.25, sm: 1.5 },
};

const touchBtnSx = {
  minHeight: 44,
  px: 2,
};

function remainingLabel(dueAt, nowMs) {
  if (!dueAt) return '—';
  const due = DateTime.fromISO(dueAt);
  if (!due.isValid) return '—';
  const diff = Math.max(0, Math.floor(due.toMillis() - nowMs) / 1000);
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = Math.floor(diff % 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatLocal(iso, zone) {
  if (!iso) return '—';
  const dt = DateTime.fromISO(iso, { zone: zone || DEFAULT_REMINDER_TIMEZONE });
  if (!dt.isValid) return iso;
  return dt.toFormat("ccc LLL d, yyyy 'at' h:mm a ZZZZ");
}

function statusChip(item) {
  const status = item?.status || 'scheduled';
  const color =
    status === 'firing' ? 'warning' :
    status === 'fired' ? 'success' :
    status === 'paused' ? 'default' :
    status === 'canceled' ? 'default' :
    item?.lastFlowError ? 'error' :
    'primary';
  let label = status;
  if (item?.lastFlowError) label = 'Flow failed';
  if (status === 'scheduled' && item?.nextFireAt && DateTime.fromISO(item.nextFireAt) < DateTime.now()) {
    label = 'overdue';
  }
  return <Chip size="small" color={color} label={label} />;
}

function emptyTimerForm(timezone) {
  return {
    title: '',
    minutes: 6,
    seconds: 0,
    timezone,
    inAppAlert: true,
  };
}

export function defaultReminderStart(timezone, now = DateTime.now()) {
  const local = now
    .setZone(timezone || DEFAULT_REMINDER_TIMEZONE)
    .plus({ minutes: 5 })
    .set({ second: 0, millisecond: 0 });
  return {
    date: local.toFormat('yyyy-LL-dd'),
    time: local.toFormat('HH:mm'),
  };
}

function emptyReminderForm(timezone) {
  const start = defaultReminderStart(timezone);
  return {
    title: '',
    date: start.date,
    time: start.time,
    timezone,
    recurrence: 'none',
    intervalHours: 6,
    daysOfWeek: [6],
    inAppAlert: true,
    flowId: '',
  };
}

export function notifyRemindersChanged() {
  try {
    window.dispatchEvent(new CustomEvent('signalbridge:reminders-changed'));
  } catch (_) {
    /* jsdom / SSR */
  }
}

export default function RemindersOverlay({ open, onClose, userData, tenantId, defaultTimezone }) {
  const { currentUser } = useAuth() || {};
  const entitled = isRemindersEnabledForUser(userData);
  const timezone = defaultTimezone || DEFAULT_REMINDER_TIMEZONE;
  const ownerUid = userData?.uid || currentUser?.uid;
  const [tab, setTab] = useState(0);
  const [timerForm, setTimerForm] = useState(() => emptyTimerForm(timezone));
  const [reminderForm, setReminderForm] = useState(() => emptyReminderForm(timezone));
  const [active, setActive] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [recent, setRecent] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [nowMs, setNowMs] = useState(Date.now());
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [open]);

  const refresh = useCallback(async () => {
    if (!entitled) return;
    setLoading(true);
    try {
      const [lists, pending] = await Promise.all([
        listReminders('all'),
        listReminderAlerts(false),
      ]);
      setActive(lists.active || []);
      setUpcoming(lists.upcoming || []);
      setRecent(lists.recent || []);
      setAlerts(pending || []);
    } catch (e) {
      toast.error(e?.message || 'Failed to load reminders');
    } finally {
      setLoading(false);
    }
  }, [entitled]);

  useEffect(() => {
    if (!open || !entitled) return;
    refresh();
  }, [open, entitled, refresh]);

  useEffect(() => {
    if (!open || !entitled || !tenantId || !ownerUid) return undefined;
    let unsub = () => {};
    try {
      const db = getFirestore();
      const q = query(
        collection(db, 'tenants', tenantId, 'reminders'),
        where('ownerUserId', '==', ownerUid)
      );
      unsub = onSnapshot(q, () => {
        refresh();
      }, () => {});
    } catch (_) {
      /* REST refresh remains */
    }
    return () => {
      try { unsub(); } catch (_) { /* ignore */ }
    };
  }, [open, entitled, tenantId, ownerUid, refresh]);

  useEffect(() => {
    let activeLoad = true;
    const loadFlows = async () => {
      if (!open || !entitled || !tenantId || !ownerUid) return;
      try {
        const admin = ['admin', 'superadmin', 'tenant_admin'].includes(userData?.role);
        let options = [];
        if (admin) {
          const all = await flowsApi.getAll(tenantId);
          options = (Array.isArray(all) ? all : all?.flows || []).map((f) => ({
            id: f.flowId || f.id,
            name: f.flowName || f.name || f.id,
          }));
        } else {
          const resp = await getFlowAssignmentsByUserPublic(ownerUid, tenantId, true);
          options = (resp?.data?.flows || []).map((f) => ({
            id: f.flowId || f.id,
            name: f.flowName || f.name || f.id,
          }));
        }
        if (activeLoad) setFlows(options.filter((o) => o.id));
      } catch (_) {
        if (activeLoad) setFlows([]);
      }
    };
    loadFlows();
    return () => {
      activeLoad = false;
    };
  }, [open, entitled, tenantId, ownerUid, userData?.role]);

  const flowMenu = useMemo(
    () => [{ id: '', name: 'None — in-app alert only' }, ...flows],
    [flows]
  );

  const saveTimer = async () => {
    const durationSeconds = (Number(timerForm.minutes) || 0) * 60 + (Number(timerForm.seconds) || 0);
    if (durationSeconds < 1) {
      toast.error('Enter a timer duration.');
      return;
    }
    setSaving(true);
    try {
      await createReminder({
        type: 'timer',
        title: timerForm.title || 'Timer',
        durationSeconds,
        timezone: timerForm.timezone,
        inAppAlert: timerForm.inAppAlert,
        flowId: null,
      });
      toast.success('Timer started');
      setTimerForm(emptyTimerForm(timerForm.timezone));
      notifyRemindersChanged();
      refresh();
    } catch (e) {
      toast.error(e?.message || 'Failed to start timer');
    } finally {
      setSaving(false);
    }
  };

  const saveReminder = async () => {
    if (!reminderForm.title.trim()) {
      toast.error('Enter a reminder title.');
      return;
    }
    const local = DateTime.fromISO(`${reminderForm.date}T${reminderForm.time}`, {
      zone: reminderForm.timezone,
    });
    if (!local.isValid) {
      toast.error('Enter a valid date and time.');
      return;
    }
    if (reminderForm.recurrence === 'none' && local <= DateTime.now().setZone(reminderForm.timezone)) {
      toast.error('That time is already in the past. Pick a future time, or change the date.');
      return;
    }
    if (reminderForm.recurrence === 'interval') {
      const hours = Number(reminderForm.intervalHours);
      if (!Number.isInteger(hours) || hours < 1 || hours > 72) {
        toast.error('Repeat every N hours must be between 1 and 72.');
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        type: 'reminder',
        title: reminderForm.title,
        dueAt: local.toUTC().toISO(),
        timezone: reminderForm.timezone,
        localTime: reminderForm.time,
        recurrence: {
          type: reminderForm.recurrence,
          daysOfWeek: reminderForm.recurrence === 'weekly' ? reminderForm.daysOfWeek : [],
          ...(reminderForm.recurrence === 'interval'
            ? { intervalHours: Number(reminderForm.intervalHours) || 6 }
            : {}),
        },
        inAppAlert: reminderForm.inAppAlert,
        flowId: reminderForm.flowId || null,
      };
      if (editingId) {
        await updateReminder(editingId, payload);
        toast.success('Reminder updated');
        setEditingId(null);
      } else {
        await createReminder(payload);
        toast.success('Reminder scheduled');
      }
      setReminderForm(emptyReminderForm(reminderForm.timezone));
      notifyRemindersChanged();
      refresh();
    } catch (e) {
      toast.error(e?.message || 'Failed to save reminder');
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (fn, success) => {
    try {
      await fn();
      if (success) toast.success(success);
      notifyRemindersChanged();
      refresh();
    } catch (e) {
      toast.error(e?.message || 'Action failed');
    }
  };

  const beginEdit = (item) => {
    const due = DateTime.fromISO(item.nextFireAt || item.dueAt, { zone: item.timezone || timezone });
    setReminderForm({
      title: item.title || '',
      date: due.isValid ? due.toFormat('yyyy-LL-dd') : emptyReminderForm(timezone).date,
      time: item.localTime || (due.isValid ? due.toFormat('HH:mm') : DEFAULT_REMINDER_LOCAL_TIME),
      timezone: item.timezone || timezone,
      recurrence: item.recurrence?.type || 'none',
      intervalHours: item.recurrence?.intervalHours || 6,
      daysOfWeek: item.recurrence?.daysOfWeek?.length ? item.recurrence.daysOfWeek : [due.isValid ? (due.weekday === 7 ? 0 : due.weekday) : 6],
      inAppAlert: item.inAppAlert !== false,
      flowId: item.flowId || '',
    });
    setEditingId(item.id);
    setTab(0);
  };

  const renderItem = (item) => (
    <Box key={item.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }} data-testid={`reminder-item-${item.id}`}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Box>
          <Typography fontWeight={600}>{item.title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {item.type === 'timer' && item.status === 'scheduled'
              ? `Remaining ${remainingLabel(item.nextFireAt || item.dueAt, nowMs)}`
              : formatLocal(item.nextFireAt || item.dueAt, item.timezone)}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {item.timezone || timezone}
            {item.recurrence?.type && item.recurrence.type !== 'none'
              ? ` · ${item.recurrence.type === 'interval'
                ? `every ${item.recurrence.intervalHours || 6} hours`
                : item.recurrence.type}`
              : ''}
            {item.flowId ? ' · Flow attached' : ''}
          </Typography>
        </Box>
        {statusChip(item)}
      </Stack>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
        {item.type === 'timer' && item.status === 'scheduled' && (
          <Button size="small" onClick={() => runAction(() => pauseReminder(item.id))}>Pause</Button>
        )}
        {item.status === 'paused' && (
          <Button size="small" onClick={() => runAction(() => resumeReminder(item.id), 'Resumed')}>Resume</Button>
        )}
        {item.status === 'fired' && (
          <>
            <Button size="small" onClick={() => runAction(() => snoozeReminder(item.id, 300), 'Snoozed 5 minutes')}>Snooze 5m</Button>
            <Button size="small" onClick={() => runAction(() => dismissReminder(item.id), 'Dismissed')}>Dismiss</Button>
          </>
        )}
        {item.type === 'reminder' && item.status === 'scheduled' && (
          <Button size="small" onClick={() => beginEdit(item)}>Edit</Button>
        )}
        {item.status !== 'canceled' && item.status !== 'completed' && (
          <Button size="small" color="warning" onClick={() => runAction(() => cancelReminder(item.id), 'Canceled')}>Cancel</Button>
        )}
        <Button size="small" color="error" onClick={() => runAction(() => deleteReminder(item.id, true), 'Deleted')}>Delete</Button>
      </Stack>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
        <Typography variant="h6">Reminders & Timers</Typography>
        <IconButton aria-label="Close reminders" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        {!entitled ? (
          <Typography color="error" sx={{ py: 2 }}>
            Reminders and timers are not enabled for your account. Ask a tenant admin to enable them in User Management.
          </Typography>
        ) : (
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
              <Tab label="Create" />
              <Tab label={`Active (${active.length})`} data-testid="reminders-tab-active" />
              <Tab label={`Upcoming (${upcoming.length})`} data-testid="reminders-tab-upcoming" />
            </Tabs>

            {tab === 0 && (
              <Stack spacing={1.5}>
                <Box component="section" sx={sectionSx} data-testid="reminders-timer-section">
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Timer</Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <TextField
                      label="Title"
                      value={timerForm.title}
                      onChange={(e) => setTimerForm({ ...timerForm, title: e.target.value })}
                      fullWidth
                      inputProps={{ 'data-testid': 'reminders-timer-title' }}
                    />
                    <TextField
                      label="Minutes"
                      type="number"
                      value={timerForm.minutes}
                      onChange={(e) => setTimerForm({ ...timerForm, minutes: e.target.value })}
                      sx={{ width: { xs: '100%', sm: 120 } }}
                      inputProps={{ 'data-testid': 'reminders-timer-minutes', min: 0 }}
                    />
                    <TextField
                      label="Seconds"
                      type="number"
                      value={timerForm.seconds}
                      onChange={(e) => setTimerForm({ ...timerForm, seconds: e.target.value })}
                      sx={{ width: { xs: '100%', sm: 120 } }}
                      inputProps={{ 'data-testid': 'reminders-timer-seconds', min: 0 }}
                    />
                  </Stack>
                  <Button
                    variant="contained"
                    onClick={saveTimer}
                    disabled={saving}
                    sx={{ ...touchBtnSx, mt: 1 }}
                    data-testid="reminders-start-timer"
                  >
                    Start Timer
                  </Button>
                </Box>

                <Box component="section" sx={sectionSx} data-testid="reminders-reminder-section">
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>{editingId ? 'Edit reminder' : 'Reminder'}</Typography>
                  <Stack spacing={1}>
                    <TextField
                      label="Title"
                      value={reminderForm.title}
                      onChange={(e) => setReminderForm({ ...reminderForm, title: e.target.value })}
                      fullWidth
                      inputProps={{ 'data-testid': 'reminders-reminder-title' }}
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <TextField
                        label="Date"
                        type="date"
                        value={reminderForm.date}
                        onChange={(e) => setReminderForm({ ...reminderForm, date: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        inputProps={{ 'data-testid': 'reminders-date' }}
                      />
                      <TextField
                        label="Time"
                        type="time"
                        value={reminderForm.time}
                        onChange={(e) => setReminderForm({ ...reminderForm, time: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        inputProps={{ 'data-testid': 'reminders-time' }}
                      />
                    </Stack>
                    <Autocomplete
                      options={IANA_TIMEZONES}
                      value={reminderForm.timezone}
                      onChange={(_, value) => setReminderForm({ ...reminderForm, timezone: value || timezone })}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Timezone"
                          inputProps={{ ...params.inputProps, 'data-testid': 'reminders-timezone' }}
                        />
                      )}
                    />
                    <FormControl fullWidth>
                      <InputLabel id="reminders-repeat-label">Repeat</InputLabel>
                      <Select
                        labelId="reminders-repeat-label"
                        id="reminders-repeat"
                        label="Repeat"
                        value={reminderForm.recurrence}
                        onChange={(e) => setReminderForm({ ...reminderForm, recurrence: e.target.value })}
                        inputProps={{ 'data-testid': 'reminders-repeat' }}
                      >
                        <MenuItem value="none">Does not repeat</MenuItem>
                        <MenuItem value="daily">Daily</MenuItem>
                        <MenuItem value="weekly">Weekly</MenuItem>
                        <MenuItem value="weekdays">Weekdays</MenuItem>
                        <MenuItem value="interval">Every few hours</MenuItem>
                      </Select>
                    </FormControl>
                    {reminderForm.recurrence === 'interval' && (
                      <Stack spacing={1}>
                        <TextField
                          label="Every N hours"
                          type="number"
                          value={reminderForm.intervalHours}
                          onChange={(e) => setReminderForm({
                            ...reminderForm,
                            intervalHours: e.target.value === '' ? '' : Number(e.target.value),
                          })}
                          inputProps={{ min: 1, max: 72, 'data-testid': 'reminders-interval-hours' }}
                          helperText="Repeats from the start time. 6 hours is typical for medicine."
                        />
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {[4, 6, 8, 12].map((hours) => (
                            <Chip
                              key={hours}
                              label={`Every ${hours} hours`}
                              color={Number(reminderForm.intervalHours) === hours ? 'primary' : 'default'}
                              onClick={() => setReminderForm({ ...reminderForm, intervalHours: hours })}
                            />
                          ))}
                        </Stack>
                      </Stack>
                    )}
                    {reminderForm.recurrence === 'weekly' && (
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {WEEKDAY_OPTIONS.map((day) => (
                          <Chip
                            key={day.value}
                            label={day.label}
                            color={reminderForm.daysOfWeek.includes(day.value) ? 'primary' : 'default'}
                            onClick={() => {
                              const next = reminderForm.daysOfWeek.includes(day.value)
                                ? reminderForm.daysOfWeek.filter((d) => d !== day.value)
                                : [...reminderForm.daysOfWeek, day.value];
                              setReminderForm({ ...reminderForm, daysOfWeek: next });
                            }}
                          />
                        ))}
                      </Stack>
                    )}
                    <FormControl fullWidth>
                      <InputLabel id="reminders-flow-label">Optional Flow</InputLabel>
                      <Select
                        labelId="reminders-flow-label"
                        id="reminders-optional-flow"
                        label="Optional Flow"
                        value={reminderForm.flowId}
                        onChange={(e) => setReminderForm({ ...reminderForm, flowId: e.target.value })}
                        inputProps={{ 'data-testid': 'reminders-optional-flow' }}
                      >
                        {flowMenu.map((flow) => (
                          <MenuItem key={flow.id || 'none'} value={flow.id}>{flow.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={reminderForm.inAppAlert}
                          onChange={(e) => setReminderForm({ ...reminderForm, inAppAlert: e.target.checked })}
                        />
                      }
                      label="In-app alert (on by default)"
                    />
                    <Typography variant="caption" color="text.secondary">
                      Defaults to about five minutes from now. Change the date if you mean tomorrow.
                      Will fire {formatLocal(
                        DateTime.fromISO(`${reminderForm.date}T${reminderForm.time}`, { zone: reminderForm.timezone }).toUTC().toISO(),
                        reminderForm.timezone
                      )}
                      {reminderForm.recurrence === 'interval'
                        ? `, then every ${Number(reminderForm.intervalHours) || 6} hours.`
                        : reminderForm.recurrence !== 'none'
                          ? `, then ${reminderForm.recurrence}.`
                          : '.'}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        onClick={saveReminder}
                        disabled={saving}
                        sx={touchBtnSx}
                        data-testid="reminders-save-reminder"
                      >
                        {editingId ? 'Update reminder' : 'Save Reminder'}
                      </Button>
                      {editingId && (
                        <Button onClick={() => { setEditingId(null); setReminderForm(emptyReminderForm(timezone)); }}>
                          Cancel edit
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </Box>
              </Stack>
            )}

            {tab === 1 && (
              <Stack spacing={1.5} data-testid="reminders-active-list">
                {loading && <Typography variant="body2">Loading…</Typography>}
                {!loading && active.length === 0 && (
                  <Typography variant="body2" color="text.secondary">No active timers.</Typography>
                )}
                {active.map(renderItem)}
              </Stack>
            )}

            {tab === 2 && (
              <Stack spacing={1.5} data-testid="reminders-upcoming-list">
                {upcoming.map(renderItem)}
                {upcoming.length === 0 && (
                  <Typography variant="body2" color="text.secondary">No upcoming reminders.</Typography>
                )}
                {recent.length > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ pt: 1 }}>Recently fired</Typography>
                    {recent.map(renderItem)}
                  </>
                )}
                {alerts.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {alerts.length} undismissed alert{alerts.length === 1 ? '' : 's'} waiting.
                  </Typography>
                )}
              </Stack>
            )}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
